import type { IVipStatusResponse } from '#api';
import { LazyModalVipLevelUp } from '#components';
import { formatBalance } from '#shared/formatters/currency';

export type TVipSocketMessage = 'updated' | 'leveled_up';

export const EVipTier = {
  Iron: 'iron',
  Steel: 'steel',
  Bronze: 'bronze',
  Silver: 'silver',
  Gold: 'gold',
  Platinum: 'platinum',
  Diamond: 'diamond',
} as const;
export type TVipTier = (typeof EVipTier)[keyof typeof EVipTier];

export interface IVipTierData {
  readonly id: TVipTier;
  readonly pointsMin: number;
  readonly pointsMax: number;
  readonly rakeback: number;
  readonly cap: number;
}

export interface IVipRewardData {
  id: string;
  date: string;
  level: TVipTier;
  claimed: boolean;
  available: boolean;
  amount: number | string;
  rakeback?: number;
}

export const useVipStore = defineStore('vip', () => {
  const { vipClubEnabled } = useRuntimeConfig().public;
  const isEnabled = ref<boolean>(vipClubEnabled);

  const tiersConfig: readonly IVipTierData[] = [
    {
      id: EVipTier.Iron,
      pointsMin: 0,
      pointsMax: 99,
      rakeback: 0,
      cap: 0,
    },
    {
      id: EVipTier.Steel,
      pointsMin: 100,
      pointsMax: 199,
      rakeback: 2.5,
      cap: 100,
    },
    {
      id: EVipTier.Bronze,
      pointsMin: 200,
      pointsMax: 499,
      rakeback: 5,
      cap: 500,
    },
    {
      id: EVipTier.Silver,
      pointsMin: 500,
      pointsMax: 1499,
      rakeback: 10,
      cap: 2000,
    },
    {
      id: EVipTier.Gold,
      pointsMin: 1500,
      pointsMax: 3999,
      rakeback: 16,
      cap: 3000,
    },
    {
      id: EVipTier.Platinum,
      pointsMin: 4000,
      pointsMax: 9999,
      rakeback: 22,
      cap: 4000,
    },
    {
      id: EVipTier.Diamond,
      pointsMin: 10000,
      pointsMax: Infinity,
      rakeback: 30,
      cap: 5000,
    },
  ];

  const currentPoints = ref<number>(0);
  const currentTier = ref<TVipTier>(EVipTier.Iron);
  const rewardList = ref<IVipRewardData[]>([]);
  const nextMonthTimestamp = ref<number>(0);

  const getTierConfigById = computed(() => (tierId: TVipTier) => {
    return tiersConfig.find((tier) => tier.id === tierId);
  });
  const getNextTierConfig = computed(() => (tierId: TVipTier) => {
    const tierIdx = tiersConfig.findIndex((tier) => tier.id === tierId);
    if (tierIdx === -1 || tierIdx + 1 >= tiersConfig.length) return null;
    return tiersConfig[tierIdx + 1];
  });

  const getUserNextTierId = computed(() => {
    return getNextTierConfig.value(currentTier.value)?.id;
  });
  const getPointsToNextUserTier = computed(() => {
    const tierConfig = getTierConfigById.value(currentTier.value);
    if (!tierConfig || tierConfig.pointsMax === Infinity) return 0;
    return Math.max(tierConfig.pointsMax - currentPoints.value + 1, 0);
  });
  const getUserProgressBarWidth = computed(() => {
    const tierConfig = getTierConfigById.value(currentTier.value);
    if (!tierConfig || tierConfig.pointsMax === Infinity) return '100%';

    const fullRange = tierConfig.pointsMax - tierConfig.pointsMin;
    const currentProgress = currentPoints.value - tierConfig.pointsMin;
    const result = Math.min((currentProgress / fullRange) * 100, 100);
    return `${result}%`;
  });
  const getUserAvailableRewardList = computed(() => {
    return rewardList.value
      .filter((reward) => reward.available)
      .map((reward) => ({
        ...reward,
        amount: formatBalance(reward.amount),
        rakeback: getTierConfigById.value(reward.level)?.rakeback || 0,
      }));
  });
  const getUserLatestUnclaimedReward = computed(() => {
    return rewardList.value.find((reward) => !reward.available);
  });

  async function getVipStatus() {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const { data, hasError } = await $authApi.post<IVipStatusResponse>('/vip/getStatus', { sessionKey });

      if (hasError || !data) {
        throw new Error('Failed to get vipStatus data');
      }

      currentPoints.value = data.points;
      currentTier.value = data.level;
      rewardList.value = data.bonuses;
      nextMonthTimestamp.value = Date.now() + Math.round(data.tillNextMonth);
    } catch (error: any) {
      console.error(error?.response?.data || error.message);
    }
  }

  async function processVipSocketEvent(message: TVipSocketMessage) {
    if (message === 'updated') {
      await getVipStatus();
    } else if (message === 'leveled_up') {
      stores.modals.open('level-up-popup', LazyModalVipLevelUp);
    }
  }

  return {
    isEnabled,
    tiersConfig,
    currentPoints,
    currentTier,
    rewardList,
    nextMonthTimestamp,

    getTierConfigById,
    getNextTierConfig,

    getUserNextTierId,
    getPointsToNextUserTier,
    getUserProgressBarWidth,
    getUserAvailableRewardList,
    getUserLatestUnclaimedReward,

    getVipStatus,
    processVipSocketEvent,
  };
});
